import { FormApi } from "final-form";
import { useState } from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { updateCertificate } from "src/services/certificateService";
import { TextareaField } from "src/ui/shared/form/TextareaField";
import { copyToClipboard } from "src/ui/utils/clipboard";
import { makeStyles } from "tss-react/mui";

import { useGetCertificate } from "@admin/hooks/useGetCertificate";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { EmteriaRoles } from "@data/roles";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import TimeService from "@services/timeService/timeService";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { UncontrolledTextField } from "@shared/form/TextField";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const useStyles = makeStyles()((theme: Theme) => ({
    section: { paddingBottom: 0 },
    formBox: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
    buttonBox: { display: "flex", justifyContent: "flex-end", marginTop: theme.spacing(2) },
    infoText: {
        marginBottom: theme.spacing(2),
        fontSize: 14,
    },
    emailLink: {
        color: theme.palette.emteria.link,
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    renewButtonBox: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: theme.spacing(2),
    },
    textContainer: {
        display: "flex",
        alignItems: "center",
        marginTop: theme.spacing(2),
    },
    copyButton: {
        marginLeft: theme.spacing(2),
    },
    infoSection: {
        marginTop: theme.spacing(3),
    },
}));

type FormValues = {
    certificateString: string;
};

const AdminCertificatePage = () => {
    const { classes } = useStyles();

    const [renewDialogOpen, setRenewDialogOpen] = useState(false);

    const { data: certificateData, isLoading: loading, refetch: refetchCertificate } = useGetCertificate();

    const { t } = useTranslation(["pages", "common"]);

    const isExpired = certificateData?.expiry ? TimeService.isDateInThePast(certificateData.expiry) : false;

    const timeLeft = certificateData?.expiry
        ? TimeService.getHumanReadableTimeDifference(certificateData.expiry, { withAffix: true })
        : t("pages:adminCertificatePage.noExpirationDate");

    const usedPercentage =
        certificateData?.licenseLimit === Infinity
            ? 0
            : ((certificateData?.usedLicenses ?? 0) * 100) / (certificateData?.licenseLimit ?? 1);

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        try {
            await updateCertificate(values.certificateString.replace(/\\n/g, "\n"));
            showSuccessToast(t("pages:adminCertificatePage.toast.certificateUpdatedSuccess"));
            await refetchCertificate();
            form.restart();
            setRenewDialogOpen(false);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };

    return (
        <PageContainer fullWidth={false}>
            {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            )}

            {!loading && !certificateData && (
                <SectionContainer className={classes.section}>
                    <SectionHeader>{t("pages:adminCertificatePage.currentCertificate")}</SectionHeader>
                    <Typography>{t("pages:adminCertificatePage.noCertificateDataAvailable")}</Typography>
                </SectionContainer>
            )}

            {!loading && certificateData && (
                <SectionContainer className={classes.section}>
                    <SectionHeader>{t("pages:adminCertificatePage.currentCertificate")}</SectionHeader>

                    <div className={classes.textContainer}>
                        <UncontrolledTextField
                            value={certificateData.publicKey}
                            label={t("pages:adminCertificatePage.publicKey")}
                            disabled
                            fullWidth
                        />
                        <SecondaryButton
                            className={classes.copyButton}
                            onClick={() => void copyToClipboard(certificateData.publicKey)}
                            tooltipProps={{ title: t("common:button.copy") }}
                            name="copy-public-key"
                        >
                            <i className="fa-solid fa-copy" />
                        </SecondaryButton>
                    </div>

                    <Box className={classes.infoSection}>
                        <Typography className={classes.infoText}>
                            {certificateData.expiry
                                ? isExpired
                                    ? t("pages:adminCertificatePage.certificateExpiredOn", {
                                          date: new Date(certificateData.expiry).toLocaleDateString(),
                                          timeLeft,
                                      })
                                    : t("pages:adminCertificatePage.certificateExpiresOn", {
                                          date: new Date(certificateData.expiry).toLocaleDateString(),
                                          timeLeft,
                                      })
                                : t("pages:adminCertificatePage.certificateHasNoExpirationDate")}
                        </Typography>
                        <Typography className={classes.infoText}>
                            • <strong>{t("pages:adminCertificatePage.totalLicensesIncluded")}</strong>{" "}
                            {certificateData.licenseLimit === Infinity
                                ? t("common:label.unlimited")
                                : certificateData.licenseLimit}
                        </Typography>
                        <Typography className={classes.infoText}>
                            • <strong>{t("pages:adminCertificatePage.totalLicensesUsed")}</strong>{" "}
                            {certificateData.usedLicenses ?? 0}{" "}
                            {certificateData.licenseLimit !== Infinity ? `(${usedPercentage.toFixed(1)}%)` : ""}
                        </Typography>
                    </Box>

                    <Box className={classes.renewButtonBox}>
                        <PrimaryButton onClick={() => setRenewDialogOpen(true)}>{t("common:button.renew")}</PrimaryButton>
                    </Box>
                </SectionContainer>
            )}

            <Form<FormValues>
                onSubmit={onSubmit}
                initialValues={{ certificateString: "" }}
                render={({ pristine, submitting, form, handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <Modal
                            open={renewDialogOpen}
                            onClose={() => setRenewDialogOpen(false)}
                            title={t("pages:adminCertificatePage.renewCertificate")}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !pristine) {
                                    e.preventDefault();
                                    void form.submit();
                                }
                            }}
                            endButton={
                                <PrimaryButton onClick={form.submit} disabled={pristine || submitting}>
                                    {t("common:button.submit")}
                                </PrimaryButton>
                            }
                        >
                            <Box className={classes.formBox}>
                                <Typography className={classes.infoText}>
                                    <Trans
                                        ns="pages"
                                        i18nKey="adminCertificatePage.renewCertificateContact"
                                        components={{
                                            a: <a href="mailto:sales@emteria.com" className={classes.emailLink} />,
                                        }}
                                    />
                                </Typography>
                                <Typography className={classes.infoText}>
                                    {t("pages:adminCertificatePage.pasteCertificateString")}
                                </Typography>

                                <TextareaField
                                    name="certificateString"
                                    label={t("pages:adminCertificatePage.certificateStringLabel")}
                                    placeholder={t("pages:adminCertificatePage.certificateStringPlaceholder")}
                                    required
                                    fullWidth
                                />
                            </Box>
                        </Modal>
                    </form>
                )}
            />
        </PageContainer>
    );
};
export default withRequiredRole(
    withMetaDescription(AdminCertificatePage, {
        title: i18next.t("pages:adminCertificatePage.metaTitle"),
        description: i18next.t("pages:adminCertificatePage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
